<template>
  <b-col>
    <b-form-group class="form-group__buttons pt-0">
      <label class="col-form-label">
        Date range
      </label>
      <v-date-picker
        v-model="selectedDate"
        mode="range"
        :input-props="{ placeholder: 'Select Date First', readonly: true }"
        :max-date="today"
        :min-date="minDate"
        :popover="{ visibility: 'focus', label: 'Date range' }"
        @input="filterEvents"
      />
    </b-form-group>
    <b-form v-if="isSelectedDate">
      <b-row v-if="isSameDate">
        <b-form-group
          label="Select Start of a Day"
          class="col-12 col-lg-6"
          :class="{ 'form-group--error': $v.selectedTime.startTime.$invalid }"
        >
          <b-form-input
            v-model="selectedTime.startTime"
            type="time"
            :disabled="!selectedDate.start || !selectedDate"
          />
          <ValidationMessage
            v-show="$v.selectedTime.startTime.$invalid"
            message="Select correct start time"
            class="form-group__message--show"
          />
        </b-form-group>
        <b-form-group
          label="Select End of a Day"
          class="col-12 col-lg-6"
          :class="{ 'form-group--error': $v.selectedTime.endTime.$invalid }"
        >
          <b-form-input
            v-model="selectedTime.endTime"
            type="time"
            :disabled="!selectedDate.start || !selectedDate"
          />
          <ValidationMessage
            v-show="$v.selectedTime.endTime.$invalid"
            message="Select correct end time"
            class="form-group__message--show"
          />
        </b-form-group>
      </b-row>
      <b-row v-else>
        <b-form-group
          label="Select Start of a Day"
          class="col-12 col-lg-6"
          :class="{ 'form-group--error': $v.selectedTime.startTime.$invalid }"
        >
          <b-form-input
            v-model="selectedTime.startTime"
            type="time"
            :disabled="!selectedDate.start || !selectedDate"
          />
          <ValidationMessage
            v-show="$v.selectedTime.startTime.$invalid"
            message="Select correct start time"
            class="form-group__message--show"
          />
        </b-form-group>
        <b-form-group
          label="Select End of a Day"
          class="col-12 col-lg-6"
          :class="{ 'form-group--error': $v.selectedTime.endTime.$invalid }"
        >
          <b-form-input
            v-model="selectedTime.endTime"
            type="time"
            :disabled="!selectedDate.start || !selectedDate"
          />
          <ValidationMessage
            v-show="$v.selectedTime.endTime.$invalid"
            message="Select correct end time"
            class="form-group__message--show"
          />
        </b-form-group>
      </b-row>
      <b-form-group class="form-group__buttons text-right">
        <b-button variant="rounded" @click="resetInputs"> Reset</b-button>
        <b-button ref="submit" variant="rounded" :disabled="$v.$invalid" @click="filterEvents">
          Submit
        </b-button>
      </b-form-group>
    </b-form>
  </b-col>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import MomentTimezone from 'moment-timezone';
import { DEFAULT_DATE, TODAY } from '@/components/shared/device-events-consts';
import {
  FORMAT_HHMMSS,
  FORMAT_MS,
  START_OF_DAY,
  END_OF_DAY
} from '@/components/shared/date-format';
import { required } from '@/lib/vuelidate-custom-validators';
import DateMethods from '@/components/mixins/date-methods';

const FAULT_EVENTS_WAVEFORMS_LIST = 'Guard1000FaultEventsWaveformsList';

export default {
  name: 'Guard1000FaultTimespanPicker',
  mixins: [DateMethods],
  props: { minDate: Date, caller: String },
  data() {
    return {
      selectedDate: { ...DEFAULT_DATE },
      selectedTime: {
        startTime: START_OF_DAY,
        endTime: END_OF_DAY
      },
      today: TODAY,
      timestampSelected: false
    };
  },
  validations() {
    return {
      selectedTime: {
        startTime: {
          required
        },
        endTime: {
          required,
          notLessThen(endTime, selectedTime) {
            return this.isSameDate ? selectedTime.startTime < endTime : true;
          }
        }
      }
    };
  },
  computed: {
    ...mapState({
      eventTimestamp: state => state.guard1000FaultWaveforms.eventTimestamp
    }),
    isSameDate() {
      const { start, end } = this.selectedDate;

      if (this.isValidDate(start) && this.isValidDate(end)) {
        return start.getTime() === end.getTime();
      }
      return false;
    },
    isSelectedDate() {
      return this.selectedDate.start && this.selectedDate.end;
    }
  },
  watch: {
    eventTimestamp(value) {
      if (this.caller === FAULT_EVENTS_WAVEFORMS_LIST && value) {
        this.timestampSelected = true;

        const { timestamp } = value;
        const eventTimestamp = timestamp * 1000;

        const timestampStart = parseInt(
          MomentTimezone(eventTimestamp)
            .subtract('3', 'second')
            .format(FORMAT_MS),
          10
        );
        const timestampEnd = parseInt(
          MomentTimezone(eventTimestamp)
            .add('3', 'second')
            .format(FORMAT_MS),
          10
        );

        this.selectedTime = {
          startTime: MomentTimezone(timestampStart).format(FORMAT_HHMMSS),
          endTime: MomentTimezone(timestampEnd).format(FORMAT_HHMMSS)
        };

        const newDayStart = MomentTimezone(timestampStart)
          .startOf('day')
          .toDate();
        const newDayEnd = MomentTimezone(timestampEnd)
          .startOf('day')
          .toDate();

        const isDateSame =
          (this.selectedDate.start || '').toString() === newDayStart.toString() &&
          (this.selectedDate.end || '').toString() === newDayEnd.toString();

        if (isDateSame) {
          this.filterEvents();
        } else {
          this.selectedDate = {
            start: newDayStart,
            end: newDayEnd
          };
        }
      } else {
        this.timestampSelected = false;
      }
    }
  },
  beforeDestroy() {
    this.setTimestamp(null);
  },
  methods: {
    ...mapMutations({
      setTimestamp: 'guard1000FaultWaveforms/setActiveEventTimestamp'
    }),
    filterEvents() {
      if (!this.isSelectedDate) {
        this.$emit('changeTimespan');

        return false;
      }
      if (!this.timestampSelected && this.isSelectedDate) {
        this.setTimestamp(null);
      }

      const { selectedDate, selectedTime } = this;

      this.$emit('changeTimespan', { selectedDate, selectedTime });

      if (this.caller === FAULT_EVENTS_WAVEFORMS_LIST) {
        this.timestampSelected = false;
      }

      return true;
    },
    resetInputs() {
      this.selectedTime = { startTime: START_OF_DAY, endTime: END_OF_DAY };
      this.selectedDate = DEFAULT_DATE;

      this.setTimestamp(null);

      this.$emit('resetTime');
    }
  }
};
</script>
